import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withStyles, makeStyles, createStyles } from "@material-ui/core/styles";
import {
  Grid,
  Checkbox,
  TextField,
  Typography,
  Chip,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import moment from "moment-timezone";
import { setDateRangePicTree, setDateRangePicTreeMinMax, setSelectedSeason, getAllSeasonData, setTimeRangePicTree } from "actions";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const useStyles = makeStyles((theme) =>
  createStyles({
    calendarWrapper: {
      paddingLeft: 0,
    },
    root: {
      backgroundColor: "#fafafa",
      zIndex: 1000,
      paddingTop: 16,
      paddingLeft: 8,
      marginTop: 0,
    },
    chip: {
      margin: theme.spacing(0.5),
      fontSize: "0.75rem",
      height: 24,
    },
    autocomplete: {
      width: 450,
      marginTop: -8,
    },
    seasonTextField: {
      "& .MuiOutlinedInput-root": {
        padding: "1px 8px",
      },
      "& .MuiOutlinedInput-input": {
        height: "1.2em",
      },
    },
  })
);

const PeriodAll = ({
  setDateRangePicTree,
  setDateRangePicTreeMinMax,
  setSelectedSeason,
  getAllSeasonData,
  allSeasonData,
  selectedSeason,
  setTimeRangePicTree,
  dateRangePicTree,
  singelSelect,
  timeselect = false,
  maxRange = null,
  allowOutOfSeason = false,
}) => {
  const classes = useStyles();
  const currentDateTime = moment().tz("Asia/Tokyo").format("YYYY-MM-DD HH:mm");
  const today = moment().tz("Asia/Tokyo").format("YYYY-MM-DD HH:mm");
  const todayFormatted = moment(today).format("YYYY-MM-DD");

  useEffect(() => {
    getAllSeasonData();
  }, []);

  useEffect(() => {
    if (allSeasonData.length === 0) return;

    if (allowOutOfSeason) {
      if (!selectedSeason || selectedSeason.length === 0) {
        setDateRangePicTreeMinMax([]);
      }
      return;
    }

    const matchedSeason = allSeasonData.find((season) => {
      const startDate = season.seasonStart;
      const endDate = season.seasonEnd;
      return today >= startDate && today <= endDate;
    });

    if (!selectedSeason || selectedSeason.length === 0) {
      const baseSeason = matchedSeason || allSeasonData[0];
      if (!baseSeason) return;

      setSelectedSeason([baseSeason]);

      const startDateOnly = baseSeason.seasonStart.split(" ")[0];
      const endDateOnly = baseSeason.seasonEnd.split(" ")[0];

      if (maxRange) {
        // 👉 maxRange ページでは「フルシーズン」を渡すだけ
        setDateRangePicTreeMinMax([startDateOnly, endDateOnly]);
        setDateRangePicTree([startDateOnly, endDateOnly]);
      } else {
        // 👉 既存ページは従来どおり「start〜min(end, today)」
        const defaultMaxDateFormatted = moment(baseSeason.seasonEnd).format(
          "YYYY-MM-DD"
        );
        const isEndDateAfterToday = moment(today).isBefore(
          defaultMaxDateFormatted
        );

        if (isEndDateAfterToday) {
          setDateRangePicTreeMinMax([startDateOnly, todayFormatted]);
          setDateRangePicTree([startDateOnly, todayFormatted]);
        } else {
          setDateRangePicTreeMinMax([startDateOnly, endDateOnly]);
          setDateRangePicTree([startDateOnly, endDateOnly]);
        }
      }

      if (timeselect) {
        const startTime = moment(baseSeason.seasonStart, "YYYY-MM-DD HH:mm:ss").format("HH:mm");
        setTimeRangePicTree([startTime, "23:59"]);
      }
    }
  }, [allSeasonData, maxRange, allowOutOfSeason, timeselect]);

  const handleSelectionChange = (event, value) => {
    const uniqueValue = value.filter((option, index, self) => {
      const isDuplicate =
        self.filter((item) => item.seasonId === option.seasonId).length > 1;
      return !isDuplicate;
    });

    const validSelections = uniqueValue.filter(
      (option, index, self) =>
        self.findIndex((o) => o.seasonId === option.seasonId) === index
    );

    if (validSelections.length > 0) {
      const selectedSeasonsData = allSeasonData.filter((s) =>
        validSelections.some((item) => item.seasonId === s.seasonId)
      );

      const earliestStart = selectedSeasonsData
        .map((s) => s.seasonStart)
        .reduce((a, b) => (a < b ? a : b));
      const latestEnd = selectedSeasonsData
        .map((s) => s.seasonEnd)
        .reduce((a, b) => (a > b ? a : b));

      // check end date if before today
      const defaultMaxDateFormatted = moment(latestEnd).format('YYYY-MM-DD');
      const isEndDateAfterToday = moment(today).isBefore(defaultMaxDateFormatted);

      if (isEndDateAfterToday) {
        setDateRangePicTreeMinMax([
          earliestStart.split(" ")[0],
          todayFormatted,
        ]);
        setDateRangePicTree([
          earliestStart.split(" ")[0],
          todayFormatted,
        ]);
      } else {
        setDateRangePicTreeMinMax([
          earliestStart.split(" ")[0],
          latestEnd.split(" ")[0],
        ]);
        setDateRangePicTree([
          earliestStart.split(" ")[0],
          latestEnd.split(" ")[0],
        ]);
      }

      // // ステートを更新
      // setSelectedSeason(updatedSelections);
      setSelectedSeason(validSelections);
    } else {
      setDateRangePicTreeMinMax([]);
      setDateRangePicTree([]);
    }
  };

  const handleSignleSeasonChange = (event, value) => {
    if (!value) return;

    const selectedSeasonsData = [value];
    const earliestStart = selectedSeasonsData
      .map((s) => s.seasonStart)
      .reduce((a, b) => (a < b ? a : b));
    const latestEnd = selectedSeasonsData
      .map((s) => s.seasonEnd)
      .reduce((a, b) => (a > b ? a : b));

    const startDateOnly = earliestStart.split(" ")[0];
    const endDateOnly = latestEnd.split(" ")[0];

    if (maxRange) {
      // 👉 maxRange ページ：フルシーズンをそのまま渡す
      setDateRangePicTreeMinMax([startDateOnly, endDateOnly]);
      setDateRangePicTree([startDateOnly, endDateOnly]);
    } else {
      // 👉 既存ページ：従来どおり start〜min(end, today)
      const defaultMaxDateFormatted = moment(latestEnd).format("YYYY-MM-DD");
      const isEndDateAfterToday = moment(today).isBefore(
        defaultMaxDateFormatted
      );

      if (isEndDateAfterToday) {
        setDateRangePicTreeMinMax([startDateOnly, todayFormatted]);
        setDateRangePicTree([startDateOnly, todayFormatted]);
      } else {
        setDateRangePicTreeMinMax([startDateOnly, endDateOnly]);
        setDateRangePicTree([startDateOnly, endDateOnly]);
      }
    }

    setSelectedSeason(selectedSeasonsData);

    if (timeselect) {
      const startTime = moment(earliestStart, "YYYY-MM-DD HH:mm:ss").format("HH:mm");
      setTimeRangePicTree([startTime, "23:59"]);
    }
  };

  return (
    <div className={classes.calendarWrapper}>
      <div className={classes.root}>
        <Grid container spacing={1} direction="row">
          <Grid item>
            シーズン：
          </Grid>
          <Grid item>
            <Autocomplete
              multiple={!singelSelect}
              id="season-autocomplete"
              options={allSeasonData}
              disableCloseOnSelect={!singelSelect}
              getOptionLabel={(option) =>
                singelSelect && selectedSeason.length > 0 && selectedSeason[0].seasonName
                ? selectedSeason[0].seasonName === undefined
                  ? ""
                  : `${selectedSeason[0].seasonName} : ${selectedSeason[0].seasonStart} ~ ${selectedSeason[0].seasonEnd}`
                : option.seasonName === undefined
                  ? ""
                  : `${option.seasonName} : ${option.seasonStart} ~ ${option.seasonEnd}`
              }
              renderOption={(option, { selected }) => {
                const isFutureSeason = moment(option.seasonStart).isAfter(currentDateTime);
                const isSelected = selectedSeason.some((selected) => selected.seasonId === option.seasonId);
                return (
                  <div>
                    {!singelSelect &&
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 8 }}
                        checked={isSelected}
                      />
                    }
                    {`${option.seasonName}: `}
                    <div
                      style={{ color: "gray", marginLeft: 48, fontSize: 12 }}
                    >
                      {`${option.seasonStart} ~ ${option.seasonEnd}`}
                    </div>
                  </div>
                );
              }}
              value={selectedSeason}
              onChange={singelSelect ? handleSignleSeasonChange : handleSelectionChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder=""
                  className={classes.seasonTextField}
                />
              )}
              renderTags={(selected, getTagProps) => {
                const visibleTags = selected.slice(0, 3);
                const extraCount = selected.length - visibleTags.length;
                return [
                  ...visibleTags.map((option, index) => (
                    <Chip
                      key={index}
                      label={option.seasonName}
                      size="small"
                      className={classes.chip}
                    />
                  )),
                  extraCount > 0 && (
                    <Typography
                      key="extra"
                      variant="body2"
                      style={{ marginLeft: 8, color: "gray" }}
                    >
                      +{extraCount}
                    </Typography>
                  ),
                ];
              }}
              disableClearable
              className={classes.autocomplete}
            />
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

PeriodAll.propTypes = {
  classes: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  periodType: state.pictree.periodType,
  allSeasonData: state.pictree.allSeasonData,
  selectedSeason: state.pictree.selectedSeason,
  dateRangePicTree: state.page.dateRangePicTree,
});

const mapDispatchToProps = (dispatch) => ({
  setDateRangePicTree: (param) => dispatch(setDateRangePicTree(param)),
  setDateRangePicTreeMinMax: (param) => dispatch(setDateRangePicTreeMinMax(param)),
  getAllSeasonData: () => dispatch(getAllSeasonData()),
  setSelectedSeason: (param) => dispatch(setSelectedSeason(param)),
  setTimeRangePicTree: (param) => dispatch(setTimeRangePicTree(param)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(null, { withTheme: true })(PeriodAll));
